import React from 'react';
import { Typography, Box, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { authApi } from '../../api/auth';
import BasicButton from '../../components/button/BasicButton';
import CustomTextField from '../../components/common/CustomTextField';
import CustomCheckbox from '../../components/common/CustomCheckbox';
import theme from '../../theme';
import { safeRedirectPath } from '../../utils/safeRedirectPath';

const schema = yup.object().shape({
  email: yup
    .string()
    .email('올바른 이메일 형식을 입력해 주세요.') // RFC 5322 기반 validation
    .required('이메일을 입력해 주세요.')
    .matches(/@[^.]*\./, '올바른 이메일 형식을 입력해 주세요.'), // 요구 사항에 의해 .의 존재 여부 추가 validation
  password: yup
    .string()
    .required('비밀번호를 입력해 주세요.')
    .min(8, '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.')
    .max(20, '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.')
    .matches(
      /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/,
      '비밀번호는 문자, 숫자, 특수문자 포함 (8~20자)로 입력해야 합니다.',
    ),
  confirmPassword: yup
    .string()
    .required('비밀번호 확인을 입력해 주세요.')
    .oneOf([yup.ref('password')], '비밀번호가 일치하지 않습니다.'),
  agreeToTerms: yup.boolean().oneOf([true], '이용약관 및 개인정보처리방침에 동의해주세요.'),
});

const Signup = () => {
  const [searchParams] = useSearchParams();
  const redirectUrl = safeRedirectPath(searchParams.get('redirectUrl'), '');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();

  const onSubmit = async (formData) => {
    try {
      const response = await authApi.checkEmailStatus(formData.email);
      const { status } = response.data;
      if (status === 'DUPLICATED') {
        setError('email', { type: 'manual', message: '이미 가입된 이메일입니다.' });
        return;
      }
      if (status === 'REJOIN_BLOCKED') {
        setError('email', { type: 'manual', message: '재가입이 불가능한 이메일입니다.' });
        return;
      }
      // sessionStorage에 임시로 저장 (보안을 위해)
      sessionStorage.setItem('signup_email', formData.email);
      sessionStorage.setItem('signup_password', formData.password);
      const detailUrl = redirectUrl
        ? `/signup/detail?redirectUrl=${encodeURIComponent(redirectUrl)}`
        : '/signup/detail';
      navigate(detailUrl, { replace: true });
    } catch (err) {
      console.error('Email check failed:', err);
      setError('email', {
        type: 'manual',
        message: '이메일 확인 중 오류가 발생했습니다.',
      });
    }
  };

  return (
    <PageLayout>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: { lg: '32.5%', md: '49%', xs: '100%' },
            minWidth: { xs: '320px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Stack sx={{ width: '100%' }} spacing={'30px'}>
            <Typography fontSize={{ lg: 24, md: 22, xs: 20 }} fontWeight="bold" textAlign="center">
              회원가입
            </Typography>

            <Stack spacing={2.5}>
              <Stack spacing={1}>
                <Typography variant="body3">
                  이메일<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  placeholder="이메일을 입력해주세요."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3">
                  비밀번호<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  type="password"
                  fullWidth
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  placeholder="문자, 숫자, 특수문자 포함 8~20자"
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3">
                  비밀번호 확인<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  type="password"
                  fullWidth
                  {...register('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  placeholder="비밀번호 재입력"
                />
              </Stack>
            </Stack>
          </Stack>

          {/* 이용약관 및 개인정보처리방침 */}
          <Box display="flex" mt={'30px'} mb={'30px'} sx={{ width: '100%' }}>
            <CustomCheckbox
              {...register('agreeToTerms')}
              error={!!errors.agreeToTerms}
              helperText={errors.agreeToTerms?.message}
              label={
                '회원가입 진행시, 이용약관 및 개인정보처리방침을 읽고 동의한 것으로 간주합니다.'
              }
            />
          </Box>
          <BasicButton
            type="submit"
            fullWidth
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            회원가입
          </BasicButton>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default Signup;
